import React, { useEffect, useState, useMemo } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Breadcrumb,
  Space,
  Button,
  Drawer,
  Statistic,
  Spin,
  Tag,
  Typography,
  Select,
  theme,
} from "antd";
import {
  HomeOutlined,
  EyeOutlined,
  SafetyCertificateOutlined,
  DashboardOutlined,
  FileProtectOutlined,
  LineChartOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { Line, Column } from "@ant-design/charts";
import { apiRequest } from "../../../../service/apiRequest";
import { showError } from "../../../../components/errorHandler";
import { HPO, POItem } from "../../../../types/HPO";

const { Title, Text } = Typography;

// Struct to represent calculated metrics per supplier
interface SupplierHealth {
  ledger_id: number;
  company_name: string;
  gstin: string;
  total_bills: number;
  inspected_bills: number;
  total_bill_cft: number;
  total_self_cft: number;
  total_accepted_cft: number;
  total_rejected_cft: number;
  total_keeda_cft: number;
  avg_accepted_pct: number;
  avg_rate: number;
  cft_difference: number;
  cft_difference_pct: number;
  net_cft_difference_amount: number;
  rank?: number;
  bills_list: HPOWithCalculated[];
}

interface HPOWithCalculated extends HPO {
  calculated_bill_cft: number;
  calculated_self_cft: number;
  calculated_accepted_cft: number;
  calculated_rejected_cft: number;
  calculated_keeda_cft: number;
  cft_difference: number;
  cft_difference_amount: number;
  accepted_pct: number;
  avg_rate: number;
  wood_names: string[];
}

// Helper to compute CFT from widths
const calculateItemCFTs = (item: POItem, woodsMap?: Map<number, any>) => {
  const woodObj = item.wood_id && woodsMap ? woodsMap.get(Number(item.wood_id)) : null;
  const woodLength = item.wood_length ? Number(item.wood_length) : (woodObj?.wood_length ? Number(woodObj.wood_length) : 0);
  const woodThickness = item.wood_thickness ? Number(item.wood_thickness) : (woodObj?.wood_thickness ? Number(woodObj.wood_thickness) : 0);

  if (!woodLength || !woodThickness) {
    return {
      bill_cft: item.pbg_cft || 0,
      self_cft: 0,
      accepted_cft: 0,
      rejected_cft: 0,
      keeda_cft: 0,
    };
  }

  // 1. Calculate Bill CFT (reported by supplier)
  const bill_cft = item.pbg_cft || 0;

  // 2. Calculate Self CFT breakdown from self_widths
  let self_cft = 0;
  let accepted_cft = 0;
  let rejected_cft = 0;
  let keeda_cft = 0;

  if (item.self_widths && Object.keys(item.self_widths).length > 0) {
    let widthTotalAccepted = 0;
    let widthTotalRejected = 0;
    let widthTotalKeeda = 0;

    Object.entries(item.self_widths).forEach(([widthStr, val]) => {
      const width = Number(widthStr);
      widthTotalAccepted += width * (val.accepted || 0);
      widthTotalRejected += width * (val.rejection || 0);
      widthTotalKeeda += width * (val.keeda || 0);
    });

    const factor = ((woodLength / 12) * woodThickness) / 144;
    accepted_cft = parseFloat((widthTotalAccepted * factor).toFixed(2));
    rejected_cft = parseFloat((widthTotalRejected * factor).toFixed(2));
    keeda_cft = parseFloat((widthTotalKeeda * factor).toFixed(2));
    self_cft = parseFloat((accepted_cft + rejected_cft + keeda_cft).toFixed(2));
  }

  return {
    bill_cft,
    self_cft,
    accepted_cft,
    rejected_cft,
    keeda_cft,
  };
};

const TimberPurchaseSupplierHealth: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [purchaseBills, setPurchaseBills] = useState<HPO[]>([]);
  const [woods, setWoods] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierHealth | null>(null);
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [drawerWoodFilter, setDrawerWoodFilter] = useState<string | null>(null);

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBills = async () => {
    try {
      setLoading(true);
      // Fetch all purchase bills and woods list
      const [billsData, woodsData] = await Promise.all([
        apiRequest<HPO[]>({
          method: "get",
          url: "get-po-bill",
        }),
        apiRequest<any[]>({
          method: "get",
          url: "woods",
        }).catch(() => []),
      ]);
      setPurchaseBills(billsData || []);
      setWoods(woodsData || []);
    } catch (err) {
      console.error(err);
      showError("Failed to fetch purchase bills.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBills();
  }, []);

  const woodsMap = useMemo(() => {
    const map = new Map<number, any>();
    woods.forEach((w) => {
      if (w.wood_id) {
        map.set(Number(w.wood_id), w);
      }
    });
    return map;
  }, [woods]);

  // Compute calculated metrics for each bill & group them by supplier (ledger)
  const supplierHealthData = useMemo(() => {
    const suppliersMap = new Map<number, HPOWithCalculated[]>();

    purchaseBills.forEach((bill) => {
      // 1. Skip non-timber bills if pbill_type is specified and is not timber
      if (bill.pbill_type && bill.pbill_type !== "timber") {
        return;
      }

      // Calculate details for this bill
      let calculated_bill_cft = 0;
      let calculated_self_cft = 0;
      let calculated_accepted_cft = 0;
      let calculated_rejected_cft = 0;
      let calculated_keeda_cft = 0;
      let total_amount = 0;
      let total_diff_amount = 0;
      const billWoodNames = new Set<string>();
      if (bill.wood_type_name) billWoodNames.add(bill.wood_type_name);

      if (bill.po_details && bill.po_details.length > 0) {
        bill.po_details.forEach((item) => {
          const cfts = calculateItemCFTs(item, woodsMap);
          calculated_bill_cft += cfts.bill_cft;
          calculated_self_cft += cfts.self_cft;
          calculated_accepted_cft += cfts.accepted_cft;
          calculated_rejected_cft += cfts.rejected_cft;
          calculated_keeda_cft += cfts.keeda_cft;
          const itemRate = item.pbg_rate || 0;
          total_amount += (item.pbg_cft || 0) * itemRate;

          if (bill.self_napi_exists) {
            total_diff_amount += (cfts.self_cft - cfts.bill_cft) * itemRate;
          }

          const itemAny = item as any;
          if (itemAny.wood_name) billWoodNames.add(itemAny.wood_name);
          if (item.wood_type_name) billWoodNames.add(item.wood_type_name);
          if (item.wood_id && woodsMap.has(Number(item.wood_id))) {
            const mappedWood = woodsMap.get(Number(item.wood_id));
            if (mappedWood?.wood_name) {
              billWoodNames.add(mappedWood.wood_name);
            }
          }
        });
      }

      const avg_rate = calculated_bill_cft > 0 ? total_amount / calculated_bill_cft : 0;
      const cft_difference = bill.self_napi_exists ? parseFloat((calculated_self_cft - calculated_bill_cft).toFixed(2)) : 0;
      const cft_difference_amount = bill.self_napi_exists ? parseFloat(total_diff_amount.toFixed(2)) : 0;
      const accepted_pct = bill.self_napi_exists && calculated_self_cft > 0
        ? parseFloat(((calculated_accepted_cft / calculated_self_cft) * 100).toFixed(2))
        : 100;

      const billWithCalculated: HPOWithCalculated = {
        ...bill,
        calculated_bill_cft: parseFloat(calculated_bill_cft.toFixed(2)),
        calculated_self_cft: parseFloat(calculated_self_cft.toFixed(2)),
        calculated_accepted_cft: parseFloat(calculated_accepted_cft.toFixed(2)),
        calculated_rejected_cft: parseFloat(calculated_rejected_cft.toFixed(2)),
        calculated_keeda_cft: parseFloat(calculated_keeda_cft.toFixed(2)),
        cft_difference,
        cft_difference_amount,
        accepted_pct,
        avg_rate: parseFloat(avg_rate.toFixed(2)),
        wood_names: Array.from(billWoodNames).filter(Boolean),
      };

      const ledgerId = bill.pbill_ledger_id;
      if (!suppliersMap.has(ledgerId)) {
        suppliersMap.set(ledgerId, []);
      }
      suppliersMap.get(ledgerId)!.push(billWithCalculated);
    });

    const suppliersList: SupplierHealth[] = [];

    suppliersMap.forEach((bills, ledgerId) => {
      const firstBill = bills[0];
      const total_bills = bills.length;
      const inspected_bills = bills.filter((b) => b.self_napi_exists).length;

      let total_bill_cft = 0;
      let total_self_cft = 0;
      let total_accepted_cft = 0;
      let total_rejected_cft = 0;
      let total_keeda_cft = 0;
      let total_cost = 0;
      let total_cft_diff_amount = 0;

      bills.forEach((b) => {
        total_bill_cft += b.calculated_bill_cft;
        total_cost += b.calculated_bill_cft * b.avg_rate;

        if (b.self_napi_exists) {
          total_self_cft += b.calculated_self_cft;
          total_accepted_cft += b.calculated_accepted_cft;
          total_rejected_cft += b.calculated_rejected_cft;
          total_keeda_cft += b.calculated_keeda_cft;
          total_cft_diff_amount += b.cft_difference_amount;
        }
      });

      const avg_rate = total_bill_cft > 0 ? parseFloat((total_cost / total_bill_cft).toFixed(2)) : 0;
      const avg_accepted_pct = total_self_cft > 0
        ? parseFloat(((total_accepted_cft / total_self_cft) * 100).toFixed(2))
        : 100;

      const cft_difference = parseFloat((total_self_cft - total_bill_cft).toFixed(2));
      const cft_difference_pct = total_bill_cft > 0
        ? parseFloat(((cft_difference / total_bill_cft) * 100).toFixed(2))
        : 0;

      suppliersList.push({
        ledger_id: ledgerId,
        company_name: firstBill.ledger_company_name || "Unknown Supplier",
        gstin: firstBill.ledger_gstin || "—",
        total_bills,
        inspected_bills,
        total_bill_cft: parseFloat(total_bill_cft.toFixed(2)),
        total_self_cft: parseFloat(total_self_cft.toFixed(2)),
        total_accepted_cft: parseFloat(total_accepted_cft.toFixed(2)),
        total_rejected_cft: parseFloat(total_rejected_cft.toFixed(2)),
        total_keeda_cft: parseFloat(total_keeda_cft.toFixed(2)),
        avg_accepted_pct,
        avg_rate,
        cft_difference,
        cft_difference_pct,
        net_cft_difference_amount: parseFloat(total_cft_diff_amount.toFixed(2)),
        bills_list: bills.sort((a, b) => b.pbill_id - a.pbill_id), // sorted newest first
      });
    });

    // Ranking algorithm (Best on Top):
    // 1. Higher Accepted % (Descending)
    // 2. Lower Absolute CFT Difference (Ascending)
    // 3. Lower Average Rate (Ascending)
    return suppliersList
      .sort((a, b) => {
        if (b.avg_accepted_pct !== a.avg_accepted_pct) {
          return b.avg_accepted_pct - a.avg_accepted_pct;
        }
        if (Math.abs(a.cft_difference) !== Math.abs(b.cft_difference)) {
          return Math.abs(a.cft_difference) - Math.abs(b.cft_difference);
        }
        return a.avg_rate - b.avg_rate;
      })
      .map((supplier, index) => ({
        ...supplier,
        rank: index + 1,
      }));
  }, [purchaseBills, woodsMap]);

  const handleOpenDrawer = (record: SupplierHealth) => {
    setSelectedSupplier(record);
    setDrawerWoodFilter(null);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setSelectedSupplier(null);
    setDrawerWoodFilter(null);
  };

  // Available wood names for the selected supplier
  const availableWoodNames = useMemo(() => {
    if (!selectedSupplier) return [];
    const set = new Set<string>();
    selectedSupplier.bills_list.forEach((bill) => {
      bill.wood_names?.forEach((name) => set.add(name));
    });
    return Array.from(set).filter(Boolean);
  }, [selectedSupplier]);

  // Filtered bills and wood-specific metric recalculation based on wood name selection in Drawer
  const displayedBillsList = useMemo(() => {
    if (!selectedSupplier) return [];
    if (!drawerWoodFilter) return selectedSupplier.bills_list;

    const filtered: HPOWithCalculated[] = [];

    selectedSupplier.bills_list.forEach((bill) => {
      // Find matching items in po_details for the chosen wood
      const matchingItems = bill.po_details?.filter((item) => {
        const names = new Set<string>();
        const itemAny = item as any;
        if (itemAny.wood_name) names.add(itemAny.wood_name);
        if (item.wood_type_name) names.add(item.wood_type_name);
        if (item.wood_id && woodsMap.has(Number(item.wood_id))) {
          const w = woodsMap.get(Number(item.wood_id));
          if (w?.wood_name) names.add(w.wood_name);
        }
        if (names.size === 0 && bill.wood_type_name) {
          names.add(bill.wood_type_name);
        }
        return names.has(drawerWoodFilter);
      });

      const billMatchesDirectly = bill.wood_type_name === drawerWoodFilter;

      if ((matchingItems && matchingItems.length > 0) || billMatchesDirectly) {
        if (matchingItems && matchingItems.length > 0) {
          let calculated_bill_cft = 0;
          let calculated_self_cft = 0;
          let calculated_accepted_cft = 0;
          let calculated_rejected_cft = 0;
          let calculated_keeda_cft = 0;
          let total_amount = 0;
          let total_diff_amount = 0;

          matchingItems.forEach((item) => {
            const cfts = calculateItemCFTs(item, woodsMap);
            calculated_bill_cft += cfts.bill_cft;
            calculated_self_cft += cfts.self_cft;
            calculated_accepted_cft += cfts.accepted_cft;
            calculated_rejected_cft += cfts.rejected_cft;
            calculated_keeda_cft += cfts.keeda_cft;
            const itemRate = item.pbg_rate || 0;
            total_amount += (item.pbg_cft || 0) * itemRate;

            if (bill.self_napi_exists) {
              total_diff_amount += (cfts.self_cft - cfts.bill_cft) * itemRate;
            }
          });

          const avg_rate = calculated_bill_cft > 0 ? total_amount / calculated_bill_cft : bill.avg_rate;
          const cft_difference = bill.self_napi_exists
            ? parseFloat((calculated_self_cft - calculated_bill_cft).toFixed(2))
            : 0;
          const cft_difference_amount = bill.self_napi_exists
            ? parseFloat(total_diff_amount.toFixed(2))
            : 0;
          const accepted_pct =
            bill.self_napi_exists && calculated_self_cft > 0
              ? parseFloat(((calculated_accepted_cft / calculated_self_cft) * 100).toFixed(2))
              : 100;

          filtered.push({
            ...bill,
            calculated_bill_cft: parseFloat(calculated_bill_cft.toFixed(2)),
            calculated_self_cft: parseFloat(calculated_self_cft.toFixed(2)),
            calculated_accepted_cft: parseFloat(calculated_accepted_cft.toFixed(2)),
            calculated_rejected_cft: parseFloat(calculated_rejected_cft.toFixed(2)),
            calculated_keeda_cft: parseFloat(calculated_keeda_cft.toFixed(2)),
            cft_difference,
            cft_difference_amount,
            accepted_pct,
            avg_rate: parseFloat(avg_rate.toFixed(2)),
            wood_names: [drawerWoodFilter],
          });
        } else {
          filtered.push({
            ...bill,
            wood_names: [drawerWoodFilter],
          });
        }
      }
    });

    return filtered;
  }, [selectedSupplier, drawerWoodFilter, woodsMap]);

  // Metrics calculated dynamically for the filtered bills in Drawer
  const drawerStats = useMemo(() => {
    if (!selectedSupplier) return null;
    const bills = displayedBillsList;
    const total_bills = bills.length;
    const inspected_bills = bills.filter((b) => b.self_napi_exists).length;
    let total_self_cft = 0;
    let total_accepted_cft = 0;
    let total_bill_cft = 0;
    let total_diff_amount = 0;

    bills.forEach((b) => {
      total_bill_cft += b.calculated_bill_cft;
      if (b.self_napi_exists) {
        total_self_cft += b.calculated_self_cft;
        total_accepted_cft += b.calculated_accepted_cft;
        total_diff_amount += b.cft_difference_amount;
      }
    });

    const avg_accepted_pct = total_self_cft > 0
      ? parseFloat(((total_accepted_cft / total_self_cft) * 100).toFixed(2))
      : 100;
    const cft_difference = parseFloat((total_self_cft - total_bill_cft).toFixed(2));
    const net_cft_difference_amount = parseFloat(total_diff_amount.toFixed(2));

    return {
      total_bills,
      inspected_bills,
      avg_accepted_pct,
      cft_difference,
      net_cft_difference_amount,
    };
  }, [selectedSupplier, displayedBillsList]);

  // Main columns
  const columns = [
    {
      title: "Rank",
      dataIndex: "rank",
      key: "rank",
      width: 80,
      align: "center" as const,
      render: (rank: number) => {
        let color = "blue";
        if (rank === 1) color = "gold";
        else if (rank === 2) color = "cyan";
        else if (rank === 3) color = "purple";
        return (
          <Tag color={color} style={{ fontWeight: "bold", fontSize: 13, borderRadius: 6 }}>
            Top {rank}
          </Tag>
        );
      },
    },
    {
      title: "Supplier / Company Name",
      key: "company_name",
      render: (_: any, record: SupplierHealth) => (
        <div>
          <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 15 }}>
            {record.company_name}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            GSTIN: <strong>{record.gstin}</strong>
          </div>
        </div>
      ),
    },
    {
      title: "Inspected Bills",
      key: "bills",
      width: 130,
      align: "center" as const,
      render: (_: any, record: SupplierHealth) => (
        <div>
          <span style={{ fontWeight: 600 }}>{record.inspected_bills}</span> / {record.total_bills}
        </div>
      ),
    },
    {
      title: "Accepted %",
      dataIndex: "avg_accepted_pct",
      key: "avg_accepted_pct",
      width: 140,
      align: "right" as const,
      sorter: (a: SupplierHealth, b: SupplierHealth) => a.avg_accepted_pct - b.avg_accepted_pct,
      render: (pct: number) => {
        let color = "#52c41a";
        if (pct < 80) color = "#ff4d4f";
        else if (pct < 95) color = "#faad14";
        return (
          <span style={{ color, fontWeight: 800, fontSize: 15 }}>
            {pct.toFixed(2)}%
          </span>
        );
      },
    },
    {
      title: "Avg Rate (₹/CFT)",
      dataIndex: "avg_rate",
      key: "avg_rate",
      width: 150,
      align: "right" as const,
      sorter: (a: SupplierHealth, b: SupplierHealth) => a.avg_rate - b.avg_rate,
      render: (rate: number) => (
        <span style={{ fontWeight: 600, color: "#1677ff" }}>
          ₹ {rate.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "CFT Difference",
      key: "cft_difference",
      width: 160,
      align: "right" as const,
      render: (_: any, record: SupplierHealth) => {
        const diff = record.cft_difference;
        const color = diff === 0 ? "inherit" : diff < 0 ? "#ff4d4f" : "#52c41a";
        return (
          <div>
            <span style={{ color, fontWeight: 700 }}>
              {diff > 0 ? `+${diff}` : diff} CFT
            </span>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>
              Bill: {record.total_bill_cft} | Self: {record.total_self_cft}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      align: "center" as const,
      render: (_: any, record: SupplierHealth) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="middle"
          onClick={() => handleOpenDrawer(record)}
          style={{ borderRadius: 6, fontWeight: 600 }}
        >
          View
        </Button>
      ),
    },
  ];

  // Drawer charts config using displayedBillsList so they respond dynamically to wood filter
  const acceptedTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .filter((b) => b.self_napi_exists)
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        accepted_pct: b.accepted_pct,
      }))
      .reverse();
  }, [displayedBillsList]);

  const priceTrendData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        rate: b.avg_rate,
      }))
      .reverse();
  }, [displayedBillsList]);

  const cftDiffData = useMemo(() => {
    if (!displayedBillsList.length) return [];
    return displayedBillsList
      .filter((b) => b.self_napi_exists)
      .map((b) => ({
        bill_no: b.pbill_bill_no || `ID-${b.pbill_id}`,
        difference: b.cft_difference,
      }))
      .reverse();
  }, [displayedBillsList]);

  return (
    <div style={{ padding: "24px 20px", minHeight: "90vh", backgroundColor: "#f8fafc" }}>
      {/* Breadcrumb Header */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item href="/reports-menu">Reports</Breadcrumb.Item>
        <Breadcrumb.Item>Supplier Health Card (Timber)</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header Panel */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: "#0f172a", letterSpacing: "-0.5px" }}>
            Timber Supplier Health Card
          </h1>
          <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: 14 }}>
            Aggregated supplier ranking computed from actual wood measurements (Self NAPI) vs bill invoice specifications.
          </p>
        </div>
      </div>

      {/* Quick Overview Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Suppliers Audited</span>}
              value={supplierHealthData.length}
              prefix={<SafetyCertificateOutlined style={{ color: "#1677ff", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#0f172a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Overall Avg Accepted Rate</span>}
              value={
                supplierHealthData.length > 0
                  ? supplierHealthData.reduce((acc, curr) => acc + curr.avg_accepted_pct, 0) / supplierHealthData.length
                  : 100
              }
              precision={2}
              suffix="%"
              prefix={<DashboardOutlined style={{ color: "#52c41a", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card bordered={false} style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.03)" }}>
            <Statistic
              title={<span style={{ color: "#64748b", fontSize: 13, fontWeight: 600, textTransform: "uppercase" }}>Total Rejection in CFT</span>}
              value={supplierHealthData.reduce((acc, curr) => acc + curr.total_rejected_cft, 0)}
              precision={2}
              suffix=" CFT"
              prefix={<CloseCircleOutlined style={{ color: "#ff4d4f", marginRight: 8 }} />}
              valueStyle={{ fontWeight: 800, color: "#ff4d4f" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Health Table */}
      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG, padding: 20, boxShadow: "0 4px 18px rgba(0,0,0,0.02)" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table<SupplierHealth>
            columns={columns}
            dataSource={supplierHealthData}
            rowKey="ledger_id"
            bordered
            size="middle"
            pagination={{
              defaultPageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} suppliers`,
            }}
          />
        )}
      </div>

      {/* Detail Drawer */}
      <Drawer
        title={
          selectedSupplier ? (
            <div>
              <Title level={4} style={{ margin: 0, color: "#0f172a" }}>
                Supplier Health: {selectedSupplier.company_name}
              </Title>
              <Text type="secondary" style={{ fontSize: 13 }}>
                GSTIN: {selectedSupplier.gstin}
              </Text>
            </div>
          ) : (
            "Supplier Detail"
          )
        }
        width={1000}
        onClose={handleCloseDrawer}
        open={drawerVisible}
        destroyOnClose
        style={{ background: "#f8fafc" }}
      >
        {selectedSupplier && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Top Wood Filter Bar */}
            <Card
              size="small"
              bordered={false}
              style={{
                borderRadius: 10,
                background: "#ffffff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <Space align="center" size="middle">
                  <span style={{ fontWeight: 700, color: "#0f172a", fontSize: 14 }}>
                    🌲 Filter by Wood Name:
                  </span>
                  <Select
                    allowClear
                    showSearch
                    placeholder="All Woods (Showing Supplier Aggregate)"
                    style={{ width: 280 }}
                    value={drawerWoodFilter}
                    onChange={(val) => setDrawerWoodFilter(val || null)}
                    options={availableWoodNames.map((name) => ({ label: name, value: name }))}
                  />
                </Space>

                {drawerWoodFilter ? (
                  <Tag
                    color="blue"
                    closable
                    onClose={() => setDrawerWoodFilter(null)}
                    style={{ fontSize: 13, padding: "3px 10px", borderRadius: 6 }}
                  >
                    Active Filter: <strong>{drawerWoodFilter}</strong>
                  </Tag>
                ) : (
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    Showing all {selectedSupplier.total_bills} bills
                  </span>
                )}
              </div>
            </Card>

            {/* Drawer Stat Row */}
            <Row gutter={16}>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Audit Completion"
                    value={drawerStats?.inspected_bills ?? selectedSupplier.inspected_bills}
                    suffix={`/ ${drawerStats?.total_bills ?? selectedSupplier.total_bills} bills`}
                    valueStyle={{ fontWeight: 700, fontSize: 18 }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Avg Accepted %"
                    value={drawerStats?.avg_accepted_pct ?? selectedSupplier.avg_accepted_pct}
                    precision={2}
                    suffix="%"
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color:
                        (drawerStats?.avg_accepted_pct ?? selectedSupplier.avg_accepted_pct) >= 95
                          ? "#52c41a"
                          : "#faad14",
                    }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Net CFT Difference"
                    value={drawerStats?.cft_difference ?? selectedSupplier.cft_difference}
                    precision={2}
                    suffix=" CFT"
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color:
                        (drawerStats?.cft_difference ?? selectedSupplier.cft_difference) < 0
                          ? "#ff4d4f"
                          : "#52c41a",
                    }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" bordered={false} style={{ borderRadius: 8 }}>
                  <Statistic
                    title="Net CFT Difference Amount"
                    value={Math.abs(
                      drawerStats?.net_cft_difference_amount ?? selectedSupplier.net_cft_difference_amount
                    )}
                    precision={2}
                    prefix={
                      (drawerStats?.net_cft_difference_amount ?? selectedSupplier.net_cft_difference_amount) < 0
                        ? "- ₹ "
                        : (drawerStats?.net_cft_difference_amount ?? selectedSupplier.net_cft_difference_amount) > 0
                          ? "+ ₹ "
                          : "₹ "
                    }
                    valueStyle={{
                      fontWeight: 700,
                      fontSize: 18,
                      color:
                        (drawerStats?.net_cft_difference_amount ?? selectedSupplier.net_cft_difference_amount) < 0
                          ? "#ff4d4f"
                          : (drawerStats?.net_cft_difference_amount ?? selectedSupplier.net_cft_difference_amount) > 0
                            ? "#52c41a"
                            : "#0f172a",
                    }}
                  />
                </Card>
              </Col>
            </Row>

            {/* Performance Charts */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
                <LineChartOutlined style={{ marginRight: 8 }} /> Performance & Trend Analysis {drawerWoodFilter && `(${drawerWoodFilter})`}
              </Title>
              {drawerWoodFilter && (
                <span style={{ fontSize: 12, color: "#1677ff", fontWeight: 600 }}>
                  Showing trends specifically for {drawerWoodFilter}
                </span>
              )}
            </div>
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={8}>
                <Card title="Accepted % Trend" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {acceptedTrendData.length > 0 ? (
                    <Line
                      data={acceptedTrendData}
                      xField="bill_no"
                      yField="accepted_pct"
                      height={180}
                      smooth
                      color="#52c41a"
                      tooltip={{ fields: ["bill_no", "accepted_pct"] }}
                      xAxis={{ label: { autoRotate: true } }}
                      yAxis={{ min: 0, max: 100 }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No audit history available.</div>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="CFT Difference Trend" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {cftDiffData.length > 0 ? (
                    <Column
                      data={cftDiffData}
                      xField="bill_no"
                      yField="difference"
                      height={180}
                      color={({ difference }: any) => (difference < 0 ? "#ff4d4f" : "#52c41a")}
                      tooltip={{ fields: ["bill_no", "difference"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No audit history available.</div>
                  )}
                </Card>
              </Col>
              <Col xs={24} lg={8}>
                <Card title="Purchase Price Trend" size="small" bordered={false} style={{ borderRadius: 8, height: 260 }}>
                  {priceTrendData.length > 0 ? (
                    <Line
                      data={priceTrendData}
                      xField="bill_no"
                      yField="rate"
                      height={180}
                      smooth
                      color="#1677ff"
                      tooltip={{ fields: ["bill_no", "rate"] }}
                      xAxis={{ label: { autoRotate: true } }}
                    />
                  ) : (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>No purchase history available.</div>
                  )}
                </Card>
              </Col>
            </Row>

            {/* Individual Purchase Bills Detail Table */}
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "8px 0 12px 0",
                }}
              >
                <Title level={5} style={{ margin: 0, color: "#1e293b" }}>
                  📋 Individual Purchase Bills Log {drawerWoodFilter && `(${drawerWoodFilter})`}
                </Title>
                <span style={{ fontSize: 12, color: "#64748b" }}>
                  Showing <strong>{displayedBillsList.length}</strong> {displayedBillsList.length === 1 ? "bill" : "bills"}
                </span>
              </div>

              <Table<HPOWithCalculated>
                dataSource={displayedBillsList}
                size="small"
                bordered
                pagination={{ defaultPageSize: 5 }}
                rowKey="pbill_id"
                columns={[
                  {
                    title: "Bill No.",
                    dataIndex: "pbill_bill_no",
                    key: "pbill_bill_no",
                    render: (val: any, record: HPOWithCalculated) => (
                      <div>
                        <strong>{val}</strong>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>Challan: {record.pbill_challan_no}</div>
                      </div>
                    ),
                  },
                  {
                    title: "Bill Date",
                    dataIndex: "pbill_bill_date",
                    key: "pbill_bill_date",
                  },
                  {
                    title: "Wood Name",
                    key: "wood_names",
                    render: (_: any, record: HPOWithCalculated) => {
                      if (!record.wood_names || record.wood_names.length === 0) {
                        return <span style={{ color: "#d9d9d9" }}>—</span>;
                      }
                      return (
                        <Space size={[0, 4]} wrap>
                          {record.wood_names.map((name) => (
                            <Tag color="geekblue" key={name} style={{ borderRadius: 4 }}>
                              {name}
                            </Tag>
                          ))}
                        </Space>
                      );
                    },
                  },
                  {
                    title: "Bill CFT",
                    dataIndex: "calculated_bill_cft",
                    key: "calculated_bill_cft",
                    align: "right" as const,
                    render: (val: number) => `${val} CFT`,
                  },
                  {
                    title: "Self CFT",
                    dataIndex: "calculated_self_cft",
                    key: "calculated_self_cft",
                    align: "right" as const,
                    render: (val: number, record) =>
                      record.self_napi_exists ? `${val} CFT` : <span style={{ color: "#d9d9d9" }}>—</span>,
                  },
                  {
                    title: "CFT Difference",
                    key: "diff",
                    align: "right" as const,
                    render: (_: any, record: HPOWithCalculated) => {
                      if (!record.self_napi_exists) return <span style={{ color: "#d9d9d9" }}>—</span>;
                      const diff = record.cft_difference;
                      const color = diff === 0 ? "inherit" : diff < 0 ? "#ff4d4f" : "#52c41a";
                      return (
                        <div>
                          <span style={{ color, fontWeight: "600" }}>{diff > 0 ? `+${diff}` : diff} CFT</span>
                          <div style={{ fontSize: 10, color }}>
                            {record.cft_difference_amount < 0
                              ? `-₹${Math.abs(record.cft_difference_amount).toFixed(2)}`
                              : record.cft_difference_amount > 0
                                ? `+₹${record.cft_difference_amount.toFixed(2)}`
                                : "₹0.00"}
                          </div>
                        </div>
                      );
                    },
                  },
                  {
                    title: "Accepted %",
                    key: "accepted_pct",
                    align: "right" as const,
                    render: (_: any, record: HPOWithCalculated) => {
                      if (!record.self_napi_exists) {
                        return <Tag color="warning">NAPI Missing</Tag>;
                      }
                      const pct = record.accepted_pct;
                      let color = "green";
                      if (pct < 80) color = "red";
                      else if (pct < 95) color = "orange";
                      return <Tag color={color}>{pct.toFixed(2)}%</Tag>;
                    },
                  },
                  {
                    title: "Avg Rate to Bill",
                    dataIndex: "avg_rate",
                    key: "avg_rate",
                    align: "right" as const,
                    render: (val: number, record: HPOWithCalculated) => (
                      <div>
                        <span style={{ fontWeight: 600 }}>₹{val.toFixed(2)}</span>
                        <div style={{ fontSize: 10, color: "#94a3b8" }}>
                          Bill: ₹{(record.calculated_bill_cft * val).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default TimberPurchaseSupplierHealth;

